import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useQueryClient } from '@tanstack/react-query'
import { MINUTOS_ATE_DESCONECTAR, dentroDoHorario, type UsuarioSessao } from '@crm/shared'
import { ErroRequisicao } from '../api/cliente'
import { chaves, useEu, useProrrogarHorario } from '../api/hooks'
import { Aviso, Botao } from './ui'

/**
 * O fim do horário de trabalho na tela (10/10/2026).
 *
 * Na hora da saída aparece "você sai do CRM em 10:00", com a
 * contagem e o botão "Mais 30 minutos", que vale uma vez por dia. Usando, o
 * aviso volta dez minutos antes da nova hora, já sem o botão. Zerou, a tela
 * vai para o login; o servidor confere a mesma hora em cada pedido, então
 * fechar o aviso ou adiantar o relógio do computador não estende nada.
 */

const CHAVE_SAIDA = 'crm-saiu-pelo-horario'

/** Lembra, para o login, que a sessão caiu pelo horário e não por erro. */
export function marcarSaidaPeloHorario(): void {
  try {
    sessionStorage.setItem(CHAVE_SAIDA, '1')
  } catch {
    // Sem armazenamento o login só não explica o motivo.
  }
}

/** Lê e esquece: o aviso aparece uma vez, na tela de login seguinte. */
export function saiuPeloHorario(): boolean {
  try {
    const marcado = sessionStorage.getItem(CHAVE_SAIDA) === '1'
    sessionStorage.removeItem(CHAVE_SAIDA)
    return marcado
  } catch {
    return false
  }
}

function mmss(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000))
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`
}

export function AvisoDeFimDoHorario({
  usuario,
  canto,
}: {
  usuario: UsuarioSessao
  /** A pilha do canto de baixo, à esquerda, que o Layout monta junto com os pedidos de apoio. */
  canto: HTMLElement | null
}) {
  const cliente = useQueryClient()
  const prorrogar = useProrrogarHorario()
  const [agora, setAgora] = useState(() => Date.now())
  const desconexao = usuario.horario?.desconexao
    ? new Date(usuario.horario.desconexao).getTime()
    : null
  const inicioDoAviso = desconexao ? desconexao - MINUTOS_ATE_DESCONECTAR * 60_000 : null

  // Fora do aviso, acorda uma vez na hora de começar; dentro, a cada segundo.
  useEffect(() => {
    if (!desconexao || !inicioDoAviso) return
    const espera =
      Date.now() < inicioDoAviso ? Math.min(inicioDoAviso - Date.now(), 2 ** 31 - 1) : 1000
    const id = window.setTimeout(() => setAgora(Date.now()), espera)
    return () => window.clearTimeout(id)
  }, [agora, desconexao, inicioDoAviso])

  const zerou = desconexao !== null && agora >= desconexao
  useEffect(() => {
    if (!zerou) return
    marcarSaidaPeloHorario()
    void cliente.invalidateQueries({ queryKey: chaves.eu })
  }, [zerou, cliente])

  if (!canto || !desconexao || !inicioDoAviso || agora < inicioDoAviso || zerou) return null

  const prorrogado = usuario.horario?.prorrogado ?? false
  const erro = prorrogar.error instanceof ErroRequisicao ? prorrogar.error.corpo.mensagem : null

  /*
    Na pilha do canto de baixo, à esquerda, sempre por último (o mais perto do
    canto). Centralizado, o cartão cobria o rodapé das janelas (Fechar e
    Agendar) justamente quando a pessoa corre para terminar algo; à direita,
    cobria o botão de enviar.
  */
  return createPortal(
    <div role="alert" className="pointer-events-auto order-last text-left text-base text-tinta-900">
      <div className="rounded-xl bg-superficie p-4 shadow-alta ring-1 ring-tinta-200">
        <p className="text-sm font-semibold">
          {prorrogado ? 'Os 30 minutos a mais estão acabando' : 'Seu horário de trabalho terminou'}
        </p>
        <p className="mt-1 text-sm text-tinta-600">
          Você sai do CRM em{' '}
          <span className="font-semibold tabular-nums text-tinta-900">
            {mmss(desconexao - agora)}
          </span>
          .{prorrogado ? ' Os 30 minutos a mais de hoje já foram usados.' : null}
        </p>
        {erro ? (
          <div className="mt-2">
            <Aviso tipo="erro">{erro}</Aviso>
          </div>
        ) : null}
        {prorrogado ? null : (
          <div className="mt-3 flex justify-end">
            <Botao
              variante="primario"
              carregando={prorrogar.isPending}
              onClick={() => prorrogar.mutate()}
            >
              Mais 30 minutos
            </Botao>
          </div>
        )}
      </div>
    </div>,
    canto,
  )
}

/**
 * O aviso amarelo de agendar fora do próprio horário (10/10/2026). Agenda do
 * mesmo jeito: "a mensagem será enviada como marcada, apenas demonstre um
 * aviso", e a pessoa corrige se quiser.
 */
export function AvisoForaDoHorario({ quando, oQue }: { quando: string; oQue: string }) {
  const { data: usuario } = useEu()
  const horario = usuario?.horario
  if (!horario || !quando) return null
  const instante = new Date(quando)
  if (Number.isNaN(instante.getTime())) return null
  if (dentroDoHorario(horario.tabela, instante, horario.fuso)) return null
  return (
    <Aviso tipo="atencao">
      Fora do seu horário de trabalho. {oQue} na hora marcada mesmo assim.
    </Aviso>
  )
}
